import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'
import { Visibility, VisibilityOff, DragIndicator } from '@mui/icons-material'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import FormTextInput from '../UniversityCRMEntryForm/FormTextInput'
import FormSelectAutocomplete from '../UniversityCRMEntryForm/FormSelectAutocomplete'
import TextEditor from '../editor/TextEditor'
import ReasonsToChooseUs from '../UniversityCRMEntryForm/ReasonsToChooseUs'
import UploadBrochure from '../UniversityCRMEntryForm/UploadBrochure'
import UploadRelevantDocument from '../UniversityCRMEntryForm/UploadRelevantDocument'
import ChangeHistory from '../UniversityCRMEntryForm/ChangeHistory'
import LanguageOfInstruction from './LanguageOfInstruction'
import EmiFinancialAidForm from './EmiFinancialAidForm'
import ScholarshipOpportunitiesAvailable from './ScholarshipOpportunitiesAvailable'
import AccreditationforCourse from './AccreditationforCourse'
import ExamModeSelector from './ExamModeSelector'
import FaqsQuestion from './FaqsQuestion'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCourseCategories, fetchCourseNames, saveCourse, updateCourseStatus } from '../../api/courseApi'
import { fetchUniversitiesNames } from '../../api/universityApi'
import { useNotification } from '../../hooks/useNotification.js'
import { FRONTEND_URL } from '../../config/config.js'
import { updateStatus } from '../../api/adminApi.js'
import { useLocation, useNavigate } from 'react-router-dom'
import { validateSchema } from '../../utils/validation/validationSchema.js'

const universityIdOptions = [
  { label: 'Amity University Online — UNI-001234', value: 'UNI-001234' },
  { label: 'Chandigarh University Online — UNI-001567', value: 'UNI-001567' },
  { label: 'Lovely Professional University (LPU) Online — UNI-001891', value: 'UNI-001891' },
  { label: 'Jain University Online — UNI-001432', value: 'UNI-001432' },
]

const degreeTypeOptions = [
  { label: 'Undergraduate', value: 'Undergraduate' },
  { label: 'Postgraduate', value: 'Postgraduate' },
  { label: 'Doctorate', value: 'Doctorate' },
]

const durationMonthsOptions = Array.from({ length: 60 }, (_, i) => ({ label: `${i + 1} month${i > 0 ? 's' : ''}`, value: `${i + 1}` }))
const durationYearsOptions = Array.from({ length: 5 }, (_, i) => ({ label: `${i + 1} year${i > 0 ? 's' : ''}`, value: `${i + 1}` }))
const learningHoursOptions = Array.from({ length: 80 }, (_, i) => ({ label: `${(i + 1) * 50} hours`, value: `${(i + 1) * 50}` }))

export const SortableItem = ({ id, label, visible, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: visible ? 1 : 0.6,
  }

  if (id === '_id') {
    return ''
  }

  return (
    <Box
      ref={setNodeRef}
      style={style}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={1}
      mb={1}
      borderBottom="1px solid #E6E7E8"
      borderRadius={1}
      bgcolor="#fff"
      sx={{ cursor: 'grab' }}
    >
      <Box display="flex" alignItems="center" {...attributes} {...listeners}>
        <DragIndicator fontSize="small" sx={{ mr: 1 }} />
        <Typography fontSize="13px">
          {label}
        </Typography>
      </Box>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          onToggle()
        }}
        sx={{ '&:hover': { backgroundColor: 'transparent' } }}
      >
        {visible ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
      </IconButton>
    </Box>
  )
}

export const handlePreview = (objectId, type) => {
  const baseUrl = FRONTEND_URL;
  if (!objectId || !type) {
    console.error("Missing baseUrl, objectId, or type for preview");
    return;
  }

  const previewUrl = `${baseUrl}/draftPreview?id=${encodeURIComponent(objectId)}&type=${encodeURIComponent(type)}`;
  window.open(previewUrl, "_blank", "noopener,noreferrer");
};

const CourseDetailsForm = ({ fields, setFields, fetchCourse,editMode }) => {
  const location = useLocation();
  const navigate = useNavigate()
  const isReadOnly = location.state?.viewOnly ?? false;
  const dispatch = useDispatch()
  const courseState = useSelector((state) => state.courseForm)
  const [courseNameOptions, setCourseNameOptions] = useState([])
  const [courseCategoryOptions, setCourseCategoryOptions] = useState([])
  const [universityIdOptions, setUniversityIdOptions] = useState([])
  const [docs, setDocs] = useState([{ title: '', file: null, uploading: false, url: '' }])
  const [uploadError, setUploadError] = useState('')
  const { showErrorNotification, showSuccessNotification } = useNotification()
  const [fieldError,setFieldError] = useState(null)

  useEffect(() => {
    fetchUniversitiesNames(setUniversityIdOptions)
    fetchCourseNames(setCourseNameOptions,{skipLimit:true})
    fetchCourseCategories(setCourseCategoryOptions)
  }, [])


  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  )

  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex(item => item.id === active.id)
    const newIndex = fields.findIndex(item => item.id === over.id)
    setFields(arrayMove(fields, oldIndex, newIndex))
  }

  const toggleVisibility = (id) => {
    setFields(
      fields.map(item =>
        item.id === id ? { ...item, visible: !item.visible } : item
      )
    )
  }

  const runValidation = async (type) => {
    const errors = await validateSchema(fields, "course");
    setFieldError(errors);
    if (type == "submit" && Object.keys(errors?.errors).length === 0) {
      handleSubmit()
    }
  };

  useEffect(() => {
    if (fieldError?.errors && Object.keys(fieldError.errors)?.length) {
      runValidation();
    }
  }, [fields]);


  const handleSubmit = () => {
    saveCourse(courseState.fields)
      .then((data) => 
        fetchCourse(data?.course?.courseId))
      .then(() => {
        showSuccessNotification('Course saved successfully');
      })
      .catch((err) => {
        showErrorNotification(err.message);
      });
  };
  const handleApproval = () => {
    const idField = courseState.fields.find(f => f.id === '_id')
    const objectId = idField?.value

    if (!objectId) {
      showErrorNotification('Please save course first')
      return
    }

    updateStatus({ id: objectId, status: "pending", type: "course" })
      .then(() => {
        showSuccessNotification('Course sent for review successfully')
        navigate(-1)
      })
      .catch((err) => {
        showErrorNotification(err.message || 'Failed to send for review')
      })
  }

  return (
    <Box display="flex" p={2} sx={{ display: 'grid', gridTemplateColumns: '1.5fr 5fr', gap: '0.5rem', }}>
      {/* LEFT SIDEBAR */}
      <Box width="100%">
        <Box borderRight="1px solid #ccc"
          sx={{ position: 'sticky', top: '9rem', display: 'block !important', height: 'calc(100vh - 10rem)', paddingLeft: '.25rem', marginLeft: '-.25rem', overflowY: 'auto', }}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={fields}
              strategy={verticalListSortingStrategy}
            >
              {fields.map((field) => (
                <SortableItem
                  key={field.id}
                  id={field.id}
                  label={field.label}
                  visible={field.visible}
                  onToggle={() => toggleVisibility(field.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Box>
      </Box>

      {/* RIGHT PANEL */}
      <Box width="100%" pl={2} sx={{
        display: 'grid',
        gap: '1.5rem',
        gridTemplateAreas: `
    "intro toc"
    "content toc"
  `,
        gridTemplateColumns: '4fr 1fr',
        gridTemplateRows: 'auto 1fr',
      }}
      >
        <Box>
          <Typography fontWeight="bold" mb={2}>
            Course Details
          </Typography>
          <Grid container spacing={2}>
            {fields.map((field) => {
              if (!field.visible) return null
              if (field.id === 'courseId') {
                const universityField = fields.find(f => f.id === 'universityId')
                const degreeField = fields.find(f => f.id === 'degreeType')

                const isUniversityVisible = universityField?.visible
                const isDegreeVisible = degreeField?.visible

                const visibleCount = [isUniversityVisible, isDegreeVisible].filter(Boolean).length

                const courseMd = visibleCount === 2 ? 4 : visibleCount === 1 ? 6 : 12
                const universityMd = isDegreeVisible ? 4 : 6
                const degreeMd = isUniversityVisible ? 4 : 6

                return (
                  <React.Fragment key={field.id}>
                    <Grid size={{ xs: 12, md: courseMd }}>
                      <FormTextInput
                        label={field.label}
                        value={field.value}
                        placeholder={field.label}
                        readOnly={field.readOnly || false}
                        fieldError={fieldError?.errors?.[field?.id]}
                        onChange={(newValue) => {
                          setFields(
                            fields.map(f =>
                              f.id === field.id ? { ...f, value: newValue } : f
                            )
                          )
                        }}
                      />
                    </Grid>

                    {isUniversityVisible && (
                      <Grid size={{ xs: 12, md: universityMd }} key={universityField.id}>
                        <FormSelectAutocomplete
                          label={universityField.label}
                          value={universityField.value}
                          options={universityIdOptions}
                          // readOnly={field.readOnly || false}
                          fieldError={fieldError?.errors?.[universityField?.id]}
                          onChange={(newValue) => {
                            setFields(
                              fields.map(f =>
                                f.id === universityField.id ? { ...f, value: newValue || '' } : f
                              )
                            )
                          }}
                        />

                      </Grid>
                    )}

                    {isDegreeVisible && (
                      <Grid size={{ xs: 12, md: degreeMd }} key={degreeField.id}>
                        <FormSelectAutocomplete
                          label={degreeField.label}
                          value={degreeField.value}
                          options={degreeTypeOptions}
                          // readOnly={field.readOnly || false}
                          fieldError={fieldError?.errors?.[degreeField?.id]}
                          onChange={(newValue) => {
                            setFields(
                              fields.map(f =>
                                f.id === degreeField.id ? { ...f, value: newValue || '' } : f
                              )
                            )
                          }}
                        />
                      </Grid>
                    )}
                  </React.Fragment>
                )
              }

              if (field.id === 'universityId' || field.id === 'degreeType') {
                const courseField = fields.find(f => f.id === 'courseId')
                if (courseField?.visible) return null

                const universityField = fields.find(f => f.id === 'universityId')
                const degreeField = fields.find(f => f.id === 'degreeType')
                const bothVisible = universityField?.visible && degreeField?.visible
                const mdSize = bothVisible ? 6 : 12

                const optionsMap = {
                  universityId: universityIdOptions,
                  degreeType: degreeTypeOptions,
                }

                return (
                  <Grid size={{ xs: 12, md: mdSize }} key={field.id}>
                    <FormSelectAutocomplete
                      label={field.label}
                      value={field.value}
                      options={optionsMap[field.id]}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      onChange={(newValue) => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue || '' } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.id === 'courseNameId') {
                const courseCategoryField = fields.find(f => f.id === 'courseCategoryId')

                return (
                  <React.Fragment key={field.id}>
                    <Grid size={{ xs: 12, md: courseCategoryField?.visible ? 6 : 12 }}>
                      <FormSelectAutocomplete
                        label={field.label}
                        value={field.value}
                        options={courseNameOptions}
                        readOnly={field.readOnly || false}
                        fieldError={fieldError?.errors?.[field?.id]}
                        onChange={(newValue) => {
                          setFields(
                            fields.map(f =>
                              f.id === field.id ? { ...f, value: newValue || '' } : f
                            )
                          )
                        }}
                      />
                    </Grid>

                    {courseCategoryField?.visible && (
                      <Grid size={{ xs: 12, md: 6 }} key={courseCategoryField.id}>
                        <FormSelectAutocomplete
                          label={courseCategoryField.label}
                          value={courseCategoryField.value}
                          options={courseCategoryOptions}
                          readOnly={field.readOnly || false}
                          fieldError={fieldError?.errors?.[courseCategoryField?.id]}
                          onChange={(newValue) => {
                            setFields(
                              fields.map(f =>
                                f.id === courseCategoryField.id ? { ...f, value: newValue || '' } : f
                              )
                            )
                          }}
                        />
                      </Grid>
                    )}
                  </React.Fragment>
                )
              }

              if (field.id === 'courseCategoryId') {
                const courseNameField = fields.find(f => f.id === 'courseNameId')
                if (courseNameField?.visible) return null

                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <FormSelectAutocomplete
                      label={field.label}
                      value={field.value}
                      options={courseCategoryOptions}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      onChange={(newValue) => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue || '' } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }
              if (field.id === 'duration_months') {
                const durationYearsField = fields.find(f => f.id === 'duration_years')
                const learningHoursField = fields.find(f => f.id === 'learning_hours')

                const isYearsVisible = durationYearsField?.visible
                const isHoursVisible = learningHoursField?.visible

                const visibleCount = [isYearsVisible, isHoursVisible].filter(Boolean).length

                const monthsMd = visibleCount === 2 ? 4 : visibleCount === 1 ? 6 : 12
                const yearsMd = isHoursVisible ? 4 : 6
                const hoursMd = isYearsVisible ? 4 : 6

                return (
                  <React.Fragment key={field.id}>
                    <Grid size={{ xs: 12, md: monthsMd }}>
                      <FormSelectAutocomplete
                        label={field.label}
                        value={field.value}
                        options={durationMonthsOptions}
                        readOnly={field.readOnly || false}
                        fieldError={fieldError?.errors?.[field?.id]}
                        onChange={(newValue) => {
                          setFields(
                            fields.map(f =>
                              f.id === field.id ? { ...f, value: newValue || '' } : f
                            )
                          )
                        }}
                      />
                    </Grid>

                    {isYearsVisible && (
                      <Grid size={{ xs: 12, md: yearsMd }} key={durationYearsField.id}>
                        <FormSelectAutocomplete
                          label={durationYearsField.label}
                          value={durationYearsField.value}
                          options={durationYearsOptions}
                          readOnly={field.readOnly || false}
                          fieldError={fieldError?.errors?.[durationYearsField?.id]}
                          onChange={(newValue) => {
                            setFields(
                              fields.map(f =>
                                f.id === durationYearsField.id ? { ...f, value: newValue || '' } : f
                              )
                            )
                          }}
                        />
                      </Grid>
                    )}

                    {isHoursVisible && (
                      <Grid size={{ xs: 12, md: hoursMd }} key={learningHoursField.id}>
                        <FormSelectAutocomplete
                          label={learningHoursField.label}
                          value={learningHoursField.value}
                          options={learningHoursOptions}
                          readOnly={field.readOnly || false}
                          fieldError={fieldError?.errors?.[learningHoursField?.id]}
                          onChange={(newValue) => {
                            setFields(
                              fields.map(f =>
                                f.id === learningHoursField.id ? { ...f, value: newValue || '' } : f
                              )
                            )
                          }}
                        />
                      </Grid>
                    )}
                  </React.Fragment>
                )
              }

              if (field.id === 'duration_years' || field.id === 'learning_hours') {
                const monthsField = fields.find(f => f.id === 'duration_months')
                if (monthsField?.visible) return null

                const yearsField = fields.find(f => f.id === 'duration_years')
                const hoursField = fields.find(f => f.id === 'learning_hours')

                const bothVisible = yearsField?.visible && hoursField?.visible
                const mdSize = bothVisible ? 6 : 12

                const optionsMap = {
                  duration_years: durationYearsOptions,
                  learning_hours: learningHoursOptions,
                }

                return (
                  <Grid size={{ xs: 12, md: mdSize }} key={field.id}>
                    <FormSelectAutocomplete
                      label={field.label}
                      value={field.value}
                      options={optionsMap[field.id]}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      onChange={(newValue) => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue || '' } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'languageOfInstruction') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <LanguageOfInstruction
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      onChange={newValue => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'emiFinancialAidForm') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <EmiFinancialAidForm
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      fieldErrors={fieldError?.errors}
                      fieldType={field?.id}
                      onChange={newValue => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'scholarshipOpportunitiesAvailable') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <ScholarshipOpportunitiesAvailable
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      fieldErrors={fieldError?.errors}
                      fieldType={field?.id}
                      onChange={newValue => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'outcomesOpportunities') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <ReasonsToChooseUs
                      label={field.label}
                      placeholder="For example, job roles, packages, industries, higher education path"
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      fieldErrors={fieldError?.errors}
                      fieldType={field?.id}
                      onChange={newValue => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'faqsQuestions') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <FaqsQuestion
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      fieldErrors={fieldError?.errors}
                      fieldType={field?.id}
                      onChange={newValue => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'examModeSelector') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <ExamModeSelector
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      fieldErrors={fieldError?.errors}
                      fieldType={field?.id}
                      onChange={newValue => {
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }}
                    />
                  </Grid>
                )
              }

              if (field.type === 'uploadBrochure') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <UploadBrochure
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      fieldErrors={fieldError?.errors}
                      fieldType={field?.id}
                      onChange={newValue =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                )
              }

              if (field.type === 'uploadRelevantDocument') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <UploadRelevantDocument
                      label="Relevant Document"
                      value={field.value}
                      readOnly={field.readOnly || false}
                      fieldError={fieldError?.errors?.[field?.id]}
                      fieldErrors={fieldError?.errors}
                      fieldType={field?.id}
                      onChange={newValue =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id
                              ? { ...f, value: Array.isArray(newValue) ? newValue : [newValue] } // Ensure consistent JSON array
                              : f
                          )
                        )
                      }
                      error={uploadError}
                      setError={setUploadError}
                    />
                  </Grid>
                )
              }

              if (field.type === 'accreditationforCourse') {
                return (
                  <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                    <AccreditationforCourse
                      label={field.label}
                      value={field.value}
                      readOnly={field.readOnly || false}
                      onChange={newValue =>
                        setFields(
                          fields.map(f =>
                            f.id === field.id ? { ...f, value: newValue } : f
                          )
                        )
                      }
                    />
                  </Grid>
                )
              }

              // DEFAULT: Text Input
              return (
                <Grid size={{ xs: 12, md: 12 }} key={field.id}>
                  <FormTextInput
                    label={field.label}
                    value={field.value}
                    placeholder={field.label}
                    readOnly={field.readOnly || false}
                    onChange={(newValue) => {
                      setFields(
                        fields.map(f =>
                          f.id === field.id ? { ...f, value: newValue } : f
                        )
                      )
                    }}
                  />
                </Grid>
              )
            })}

            <Box display="flex" gap={2} sx={{ justifyContent: 'end', mt: 3 }}>

                <>
                  {/* <Button sx={{ border: '1px solid #15243E', minHeight: 'auto', padding: '4px 15px', color: '#15243E', background: '#fff', fontSize: '14px', textTransform: 'capitalize', }}
                // onClick={() => handlePreview(objectId, type)}
              >
                Preview
              </Button> */}
                  {(!isReadOnly || editMode) && <Button sx={{ border: '1px solid #1075FF', minHeight: 'auto', padding: '4px 15px', color: '#1075FF', background: '#fff', fontSize: '14px', textTransform: 'capitalize', }} onClick={()=>runValidation("submit")} disabled={isReadOnly && !editMode}>
                    Save
                  </Button>}
                  {/* <Button sx={{ background: '#098B34', color: '#fff', minHeight: 'auto', padding: '4px 15px', fontSize: '14px', textTransform: 'capitalize', }}>
                Publish
              </Button> */}
                  {!isReadOnly && <Button sx={{ background: '#0463EF', color: '#fff', minHeight: 'auto', padding: '4px 15px', fontSize: '14px', textTransform: 'capitalize', }} onClick={handleApproval} disabled={isReadOnly}>
                    Send for Approval
                  </Button>}
                </>
                
            </Box>
          </Grid>
        </Box>
        {/* <ChangeHistory /> */}
      </Box>
    </Box>
  )
}

export default CourseDetailsForm
